// components/users/UsersTable.jsx

import axios from "axios";
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { RefreshCw, Search } from "lucide-react";
import UserEditModal from "../pages/Settings/UserEditModal";
import AddUserModal from "../pages/Settings/AddUserModal";
import MantleDataGrid, { gcell } from "./MantleDataGrid";
import { isAdmin } from "../utils/permissions";

// ---------------------------------------------------------------------------
// Manage User Accounts — users + EVERY role/site assignment they hold (the API
// no longer filters assignments to the selected site). Rendered on
// MantleDataGrid (same engine as Structures); roles show as "Role @ Site"
// pills. Click a header to sort, click Edit to open the user modal.
// ---------------------------------------------------------------------------

const SORT_DIRS = { asc: 1, desc: -1 };

const STATUS_OPTIONS = [
  { value: "all", label: "All statuses" },
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
];

// Canvas cell accents.
const T_ACTIVE = { textDark: "#059669", bgCell: "rgba(5, 150, 105, 0.10)" };
const T_INACTIVE = { textDark: "#dc2626", bgCell: "rgba(220, 38, 38, 0.10)" };
const T_EDIT = { textDark: "#2563eb" };
const T_MUTED = { textDark: "#94a3b8" };

const SORTABLE = { uid: "uid", uname: "uname", uemail: "uemail", status: "uis_active" };

function StatCard({ label, value }) {
  return (
    <div className="bg-gray-50 dark:bg-slate-800 rounded-lg px-4 py-3 flex-1 min-w-[72px]">
      <p className="text-[10px] uppercase tracking-widest text-gray-400 mb-1">{label}</p>
      <p className="text-xl font-medium text-gray-800 dark:text-slate-100">{value}</p>
    </div>
  );
}

export default function UsersTable() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [roleFilter, setRoleFilter] = useState("all");
  const [sort, setSort] = useState({ column: "uid", dir: "asc" });

  const [editingUser, setEditingUser] = useState(null);
  const [addingUser, setAddingUser] = useState(null);

  // -------------------------------------------------------------------------
  // Data fetching
  // -------------------------------------------------------------------------

  const loadUsers = useCallback(async () => {
    setLoading(true);
    setError(null);

    const API_URL = window.MANTLE_CONFIG.API_URL ?? "http://localhost:5000/api";

    try {
      const token = localStorage.getItem("jwt");
      let loadingURL = API_URL + "/users/";
      // The trailing :code param is kept for route compatibility but the API
      // ignores it — ALL role/site assignments come back for every user.
      const siteCode = localStorage.getItem("siteCode") ?? "ALL";

      if (isAdmin()) {
        loadingURL += `load-all-users`;
      } else {
        const uid = localStorage.getItem("uid");
        loadingURL += `load-user/${uid}`;
      }

      loadingURL += "/" + siteCode;

      axios.get(`${loadingURL}`,
        {
          headers: {
            'Authorization': 'Bearer ' + token,
            'x-db-name': window.MANTLE_CONFIG.TENANT_DB
          },
        }
      ).then(({ data }) => {
        const normalized = Object.values(data.data ?? data);
        setUsers(normalized);
      }).catch((err) => {
        setError(err.response?.data?.message ?? "Failed to load users.");
      }).finally(() => {
        setLoading(false);
      });
    } catch (err) {
      setError(err.message || "Failed to load users!");
      setLoading(false);
    }
  }, []);

  const createUser = (data) => {
    const body = {
      'uname': data.username,
      'email': data.email,
      'passwd': data.password,
      'confPasswd': data.password // already checked by form validation in modal
    };

    const API_URL = window.MANTLE_CONFIG.API_URL + "/users/" || "http://localhost:5000/api/users/";
    const token = localStorage.getItem("jwt");
    const postURL = API_URL + "register";

    axios.post(postURL, body, {
      headers: {
        'Authorization': 'Bearer ' + token,
        'x-db-name': window.MANTLE_CONFIG.TENANT_DB
      }
    }).catch((err) => {
      setError(err.response?.data?.message ?? "Failed to add a user.");
    }).finally(() => {
      loadUsers();
    });
  };

  useEffect(() => { loadUsers(); }, [loadUsers]);

  // -------------------------------------------------------------------------
  // Role filter options / filtering + sorting (top-toolbar driven)
  // -------------------------------------------------------------------------

  const roleOptions = useMemo(() => {
    const seen = new Map();
    users.forEach((u) =>
      u.roles?.forEach((r) => {
        if (r.rid && !seen.has(r.code)) seen.set(r.code, r.name);
      })
    );
    return [...seen.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [users]);

  const filteredUsers = useMemo(() => {
    const q = search.toLowerCase().trim();

    const filtered = users.filter((u) => {
      const matchSearch =
        !q ||
        u.uname.toLowerCase().includes(q) ||
        u.uemail.toLowerCase().includes(q) ||
        String(u.uid).includes(q);

      const matchStatus =
        statusFilter === "all" ||
        (statusFilter === "active" ? u.uis_active : !u.uis_active);

      const matchRole =
        roleFilter === "all" || u.roles?.some((r) => r.code === roleFilter);

      return matchSearch && matchStatus && matchRole;
    });

    return [...filtered].sort((a, b) => {
      let av = a[sort.column];
      let bv = b[sort.column];
      if (typeof av === "boolean") { av = av ? 1 : 0; bv = bv ? 1 : 0; }
      if (typeof av === "string") { av = av.toLowerCase(); bv = bv.toLowerCase(); }
      return (av < bv ? -1 : av > bv ? 1 : 0) * SORT_DIRS[sort.dir];
    });
  }, [users, search, statusFilter, roleFilter, sort]);

  const filteredRef = useRef([]);
  filteredRef.current = filteredUsers;

  const stats = useMemo(() => ({
    total: users.length,
    active: users.filter((u) => u.uis_active).length,
    withRoles: users.filter((u) => u.roles?.some((r) => r.rid)).length,
    showing: filteredUsers.length,
  }), [users, filteredUsers]);

  const handleSort = useCallback((column) => {
    setSort((prev) =>
      prev.column === column
        ? { column, dir: prev.dir === "asc" ? "desc" : "asc" }
        : { column, dir: "asc" }
    );
  }, []);

  const hasFilters = search || statusFilter !== "all" || roleFilter !== "all";

  // -------------------------------------------------------------------------
  // Grid wiring
  // -------------------------------------------------------------------------

  const gcols = useMemo(() => {
    const arrow = (key) => (sort.column === SORTABLE[key] ? (sort.dir === "asc" ? " ▲" : " ▼") : "");
    return [
      { id: "uid", title: `UID${arrow("uid")}`, width: 70 },
      { id: "uname", title: `User${arrow("uname")}`, width: 150, grow: 0.5 },
      { id: "uemail", title: `Email${arrow("uemail")}`, width: 230, grow: 0.7 },
      { id: "status", title: `Status${arrow("status")}`, width: 96 },
      { id: "roles", title: "Roles & Sites", width: 380, grow: 2 },
      { id: "edit", title: "", width: 64 },
    ];
  }, [sort]);
  const colIds = useMemo(() => gcols.map((c) => c.id), [gcols]);

  const getCellContent = useCallback(([col, row]) => {
    const id = colIds[col];
    const u = filteredUsers[row];
    if (!u) return gcell.text("", { readonly: true });
    switch (id) {
      case "uid": return gcell.text(`#${u.uid}`, { readonly: true, theme: T_MUTED });
      case "uname": return gcell.text(u.uname, { readonly: true });
      case "uemail": return gcell.text(u.uemail, { readonly: true });
      case "status":
        return gcell.text(u.uis_active ? "Active" : "Inactive", {
          readonly: true, theme: u.uis_active ? T_ACTIVE : T_INACTIVE,
        });
      case "roles": {
        const pills = (u.roles || [])
          .filter((r) => r.rid)
          .map((r) => `${r.name} @ ${r.site_name || (r.site_code ?? null) || "All sites"}`);
        return pills.length
          ? gcell.bubble(pills)
          : gcell.text("no roles assigned", { readonly: true, theme: T_MUTED });
      }
      case "edit": return gcell.text("Edit ▸", { readonly: true, theme: T_EDIT });
      default: return gcell.text("", { readonly: true });
    }
  }, [colIds, filteredUsers]);

  const onCellClicked = useCallback(([col, row]) => {
    const u = filteredRef.current[row];
    if (!u) return;
    if (colIds[col] === "edit" && isAdmin()) setEditingUser(u);
  }, [colIds]);

  const onHeaderClicked = useCallback((colIndex) => {
    const key = SORTABLE[colIds[colIndex]];
    if (key) handleSort(key);
  }, [colIds, handleSort]);

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  return (
    <div className="p-6 max-w-6xl mx-auto">

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2 mb-4">

        {/* Search */}
        <div className="relative flex-1 min-w-[200px] max-w-xs">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search users…"
            className="w-full pl-8 pr-3 py-2 text-sm border border-gray-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-100 focus:outline-none focus:border-blue-400 placeholder:text-gray-300 dark:placeholder:text-slate-500"
          />
        </div>

        {/* Status filter */}
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="text-sm border border-gray-200 dark:border-slate-600 rounded-lg px-3 py-2 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-100 focus:outline-none focus:border-blue-400 cursor-pointer"
        >
          {STATUS_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>

        {/* Role filter */}
        <select
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value)}
          className="text-sm border border-gray-200 dark:border-slate-600 rounded-lg px-3 py-2 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-100 focus:outline-none focus:border-blue-400 cursor-pointer"
        >
          <option value="all">All roles</option>
          {roleOptions.map(([code, name]) => (
            <option key={code} value={code}>{name}</option>
          ))}
        </select>

        {/* Reload */}
        <button
          onClick={loadUsers}
          disabled={loading}
          className="ml-auto flex items-center gap-1.5 text-sm px-3 py-2 border border-gray-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
          Reload
        </button>
        <button onClick={() => setAddingUser(true)} disabled={!isAdmin()}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-green-200 dark:border-green-800 bg-white dark:bg-slate-800 text-sm text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/30 hover:border-green-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >Add User</button>
      </div>

      {/* Stats */}
      <div className="flex gap-2 mb-4 flex-wrap">
        <StatCard label="Total users" value={stats.total} />
        <StatCard label="Active" value={stats.active} />
        <StatCard label="With roles" value={stats.withRoles} />
        <StatCard label="Showing" value={stats.showing} />
      </div>

      {/* Error */}
      {error && (
        <div className="mb-4 px-4 py-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg">
          {error}
        </div>
      )}

      {/* Grid */}
      <div
        className="max-h-[70vh] flex flex-col border border-gray-200 dark:border-slate-700 rounded-xl overflow-hidden bg-white dark:bg-slate-900"
      >
        {loading && !users.length ? (
          <div className="py-14 text-center text-sm text-gray-400">Loading users …</div>
        ) : filteredUsers.length === 0 ? (
          <div className="py-14 text-center text-sm text-gray-400">
            {hasFilters ? "No users match your filters." : "No users found."}
          </div>
        ) : (
          <MantleDataGrid
            gridId="users"
            fitHeight
            columns={gcols}
            rowCount={filteredUsers.length}
            getCellContent={getCellContent}
            onCellClicked={onCellClicked}
            onHeaderClicked={onHeaderClicked}
            rowHeight={34}
            hint="Every role & site assignment is shown. Click a header to sort, Edit to manage a user."
          />
        )}
      </div>

      {editingUser && (
        <UserEditModal
          user={editingUser}
          onSave={(updated) => { // use Axios to post updated user to backend

            // update uname in localStorage for current user
            if (updated.uid === parseInt(localStorage.getItem('uid'))) {
              localStorage.setItem('uname', updated.uname);
            }

            try {
              const body = {
                uid: updated.uid,
                uname: updated.uname,
                uemail: updated.uemail,
                uis_active: updated.uis_active
              };

              axios.post(window.MANTLE_CONFIG.API_URL + '/users/update-user', body,
                {
                  headers: {
                    'Authorization': 'Bearer ' + localStorage.getItem('jwt'),
                    'x-db-name': window.MANTLE_CONFIG.TENANT_DB
                  }
                }
              ).then(response => {
                if (response.data) {
                  if (response.data.err) {
                    setError(response.data.err);
                  } else {
                    setError('');
                  }

                  // Reload rather than splice — the update response has no
                  // assignment data, and roles may have changed in the modal.
                  loadUsers();
                  setEditingUser(null);
                } else {
                  setError("No User data returned");
                }
              }).catch(error => {
                setError("Query Failure: " + error.message);
              });
            } catch (err) {
              setError(err.message);
            }
          }}

          onChangePassword={(uid, newPassword, confirmPassword) =>
            axios.post(
              window.MANTLE_CONFIG.API_URL + '/users/change-password',
              { uid, new_password: newPassword, confirm_password: confirmPassword },
              {
                headers: {
                  'Authorization': 'Bearer ' + localStorage.getItem('jwt'),
                  'x-db-name': window.MANTLE_CONFIG.TENANT_DB,
                },
              }
            )
          }
          onClose={() => { setEditingUser(null); loadUsers(); }}
        />
      )}

      {addingUser && (
        <AddUserModal
          onClose={() => setAddingUser(false)}
          onSubmit={(data) => createUser(data)}
        />
      )}

    </div>
  );
}
